import React, { createContext, useContext, useEffect, useState } from 'react';
import { StatusCode } from '../../components/Alert.tsx';
import AlimentsController from '../../controllers/AlimentsController.ts';
import { Aliment } from '../../models/aliments/aliment.ts';
import { AlimentSearchParam } from '../../models/searchParams/alimentSearchParam.ts';
import { TableSearchParam } from '../../models/searchParams/tableSearchParam.ts';
import { useAlert } from '../alertProvider.tsx';

type AlimentsContextType = {
    isLoaded?: boolean;
    aliments: Aliment[];
    totalCount: number;
    tableSearchParam: TableSearchParam;
    setTableSearchParam: (tableSearchParam: TableSearchParam) => void;
    searchParam: AlimentSearchParam;
    setSearchParam: (baseSearchParam: AlimentSearchParam) => void;
    deleteAliment: (alimentID: number) => {};
};

const AlimentsContext = createContext<AlimentsContextType>({} as AlimentsContextType);

export const AlimentsContextProvider = ({ children }: { children: any }) => {
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [aliments, setAliments] = useState<Aliment[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [tableSearchParam, setTableSearchParam] = useState<TableSearchParam>({ offset: 0, limit: 25 });
    const [searchParam, setSearchParam] = useState<AlimentSearchParam>({} as AlimentSearchParam);
    const { showAlert } = useAlert();

    const fetchAliments = async () => {
        try {
            setIsLoaded(false);
            const result = await AlimentsController.get(tableSearchParam, searchParam);
            setAliments(result.aliments);
            setTotalCount(result.totalCount);
        } catch (error) {
            console.error('Error fetching aliments:', error);
        } finally {
            setIsLoaded(true);
        }
    };
    useEffect(() => {
        fetchAliments();
    }, [tableSearchParam, searchParam]);

    const deleteAliment = async (alimentID: number) => {
        try {
            const result = await AlimentsController.delete(alimentID);
            if (result.notificationStatus == StatusCode.success)
                fetchAliments();
            else
                showAlert(result.notificationStatus, result.notificationMessage);
        } catch (error) {
            console.error('Error deleting aliment:', error);
        } finally {
            setIsLoaded(true);
        }
    }

    const context: AlimentsContextType = {
        isLoaded,
        aliments,
        totalCount,
        tableSearchParam,
        setTableSearchParam,
        searchParam,
        setSearchParam,
        deleteAliment
    }

    return (
        <AlimentsContext.Provider value={context}>
            {children}
        </AlimentsContext.Provider>
    )
}

export const useAlimentsContext = () => useContext(AlimentsContext)
